import React, { useState, useEffect, useRef } from 'react';
import Calling from '../../../api/Calling';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IoCheckmarkDone } from 'react-icons/io5';
import { IoMdCheckmark } from 'react-icons/io';

const ChatRead = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('id');

    const [loader, setLoader] = useState(false);
    const [messages, setMessages] = useState([]);
    const [readStatus, setReadStatus] = useState({ user: false, admin: false });
    const [newMessage, setNewMessage] = useState('');
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [userInfo, setUserInfo] = useState({
        u_name: '',
        u_profile: '',
        u_email: '',
        _id: '',
        isActive: false
    });
    const [isRefreshing, setIsRefreshing] = useState(false);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const refreshTimerRef = useRef(null);

    // Extract userId from chat details on first load
    useEffect(() => {
        if (userId) {
            console.log(`User ID changed to ${userId}, resetting page to 1`);
            setPage(1); // Reset page when userId changes
            setMessages([]); // Clear messages
            fetchChatMessages(1);
        }
    }, [userId]);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (messages.length > 0 && page === 1) {
            scrollToBottom();
        }
    }, [messages, page]);

    // Fetch chat messages for a specific user
    const fetchChatMessages = async (pageNum = page) => {
        try {
            if (pageNum === 1) {
                setLoader(true);
            } else {
                setLoadingMore(true);
            }

            console.log(`Fetching messages for page ${pageNum}`);

            // Just fetch messages and use chatScreen property
            const resp = await Calling.getChatsById(userId, pageNum, 50);

            if (resp.status) {
                const newMessages = resp.messages || [];
                console.log(`Received ${newMessages.length} messages for page ${pageNum}`);

                if (pageNum === 1) {
                    // For first page, replace all messages
                    setMessages(newMessages);
                } else {
                    // For pagination, add messages to the beginning
                    setMessages(prevMessages => {
                        // Create a map of existing message IDs to avoid duplicates
                        const existingIds = new Set(prevMessages.map(msg => msg._id));

                        // Filter out any duplicates from new messages
                        const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg._id));

                        console.log(`Adding ${uniqueNewMessages.length} unique messages to the beginning`);
                        return [...uniqueNewMessages, ...prevMessages].slice(0, 50 * pageNum);
                    });
                }

                setReadStatus(resp.readStatus || { user: false, admin: false });
                setTotalPages(resp.totalPages || 1);

                // Update page status based on current page and total pages
                console.log(`Current page: ${pageNum}, Total pages: ${resp.totalPages || 1}`);

                // Update user info from API response
                if (resp.userInfo) {
                    setUserInfo(prevInfo => ({
                        ...prevInfo,
                        u_name: resp.userInfo.u_name || prevInfo.u_name,
                        u_profile: resp.userInfo.u_profile || prevInfo.u_profile,
                        u_email: resp.userInfo.u_email || prevInfo.u_email,
                        _id: resp.userInfo._id || prevInfo._id,
                        // Get chatScreen status from userInfo object
                        isActive: resp.userInfo.chatScreen === true
                    }));
                }
            } else {
                toast.error(resp.message || 'Failed to load messages');
            }
        } catch (error) {
            console.error('Error fetching chat messages:', error);
            toast.error('Failed to load messages');
        } finally {
            if (pageNum === 1) {
                setLoader(false);
            } else {
                setLoadingMore(false);
            }
        }
    };

    // Navigate to user profile
    const navigateToUserProfile = () => {
        if (userInfo && userInfo._id) {
            navigate(`/manage/user/all/info/${userInfo._id}`);
        } else {
            toast.error('User information not available');
        }
    };

    // Set up chat refresh interval
    useEffect(() => {
        if (!userId) return;

        // Set up refresh interval (every 10 seconds)
        refreshTimerRef.current = setInterval(() => {
            if (userId) refreshMessagesSilently();
        }, 10000);

        return () => {
            if (refreshTimerRef.current) {
                clearInterval(refreshTimerRef.current);
            }
        };
    }, [userId]);

    // Refresh only the latest messages (first page) silently
    const refreshMessagesSilently = async () => {
        try {
            setIsRefreshing(true);
            const resp = await Calling.getChatsById(userId, 1, 50);

            if (resp.status) {
                // Get the newest messages from the response
                const newMessages = resp.messages || [];

                // Check if we have new messages by comparing the latest message IDs
                let hasNewMessages = false;
                if (messages.length > 0 && newMessages.length > 0) {
                    const latestOldMessageId = messages[messages.length - 1]._id;
                    const latestNewMessageId = newMessages[newMessages.length - 1]._id;
                    hasNewMessages = latestOldMessageId !== latestNewMessageId;
                }

                if (hasNewMessages) {
                    console.log("New messages detected, updating chat");
                    // Preserve our older messages and just add the new ones that aren't already there
                    setMessages(prevMessages => {
                        // Create a map of existing message IDs
                        const existingIds = new Set(prevMessages.map(msg => msg._id));

                        // Filter new messages that don't exist in current messages
                        const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg._id));

                        // Combine old messages with unique new ones, limited to 50 messages total
                        const combinedMessages = [...prevMessages, ...uniqueNewMessages];
                        return combinedMessages.slice(Math.max(0, combinedMessages.length - 50));
                    });
                }

                setReadStatus(resp.readStatus || { user: false, admin: false });
                setTotalPages(resp.totalPages || 1);

                // Update user info
                if (resp.userInfo) {
                    setUserInfo(prevInfo => ({
                        ...prevInfo,
                        u_name: resp.userInfo.u_name || prevInfo.u_name,
                        u_profile: resp.userInfo.u_profile || prevInfo.u_profile,
                        u_email: resp.userInfo.u_email || prevInfo.u_email,
                        _id: resp.userInfo._id || prevInfo._id,
                        // Update activity status from userInfo
                        isActive: resp.userInfo.chatScreen === true
                    }));
                }
            }
        } catch (error) {
            console.error('Error refreshing chat:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    // Refresh messages with loader (for manual refresh)
    const refreshMessages = async () => {
        try {
            setLoader(true);
            const resp = await Calling.getChatsById(userId, 1, 50);

            if (resp.status) {
                const newMessages = resp.messages || [];

                // For manual refresh, we want to preserve pagination history
                // but also ensure we have the latest messages
                setMessages(prevMessages => {
                    // Get all old messages except those that might overlap with new ones
                    const oldMessageIds = new Set(newMessages.map(msg => msg._id));
                    const olderMessages = prevMessages.filter(msg => !oldMessageIds.has(msg._id));

                    // Combine older messages with new ones
                    return [...olderMessages, ...newMessages].slice(Math.max(0, olderMessages.length + newMessages.length - 50));
                });

                setReadStatus(resp.readStatus || { user: false, admin: false });
                setTotalPages(resp.totalPages || 1);

                // Update user info including activity status
                if (resp.userInfo) {
                    setUserInfo(prevInfo => ({
                        ...prevInfo,
                        u_name: resp.userInfo.u_name || prevInfo.u_name,
                        u_profile: resp.userInfo.u_profile || prevInfo.u_profile,
                        u_email: resp.userInfo.u_email || prevInfo.u_email,
                        _id: resp.userInfo._id || prevInfo._id,
                        // Update activity status from userInfo
                        isActive: resp.userInfo.chatScreen === true
                    }));
                }

                // Scroll to bottom after refresh
                setTimeout(scrollToBottom, 300);
            }
        } catch (error) {
            console.error('Error refreshing chat:', error);
            toast.error('Failed to refresh messages');
        } finally {
            setLoader(false);
        }
    };

    // Load more (older) messages
    const loadMoreMessages = () => {
        if (page < totalPages && !loadingMore) {
            const nextPage = page + 1;
            console.log(`Loading more messages, moving to page ${nextPage} of ${totalPages}`);
            setPage(nextPage);
            fetchChatMessages(nextPage);
        }
    };

    // Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            toast.error('Only JPG, JPEG, and PNG images are allowed');
            return;
        }

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Image size must be less than 2MB');
            return;
        }

        setImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Remove selected image
    const removeImage = () => {
        setImage(null);
        setImagePreview('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Send a new message
    const sendMessage = async (e) => {
        if (e) e.preventDefault();

        if (!newMessage.trim() && !image) {
            toast.error('Please enter a message or select an image');
            return;
        }

        try {
            setLoader(true);

            const formData = new FormData();
            formData.append('userId', userId);
            formData.append('text', newMessage);

            if (image) {
                formData.append('image', image);
            }

            const resp = await Calling.sendUserChat(formData);

            if (resp.status) {
                setNewMessage('');
                removeImage();

                // After sending a message, we need to refresh while preserving older messages
                try {
                    const chatResp = await Calling.getChatsById(userId, 1, 50);

                    if (chatResp.status) {
                        // Update messages while preserving older ones
                        setMessages(prevMessages => {
                            const newMessages = chatResp.messages || [];

                            // Identify messages we already have
                            const existingIds = new Set(newMessages.map(msg => msg._id));

                            // Keep older messages that aren't in the new batch
                            const olderMessages = prevMessages.filter(msg => !existingIds.has(msg._id));

                            // Combine and limit to reasonable amount
                            return [...olderMessages, ...newMessages].slice(Math.max(0, olderMessages.length + newMessages.length - 50));
                        });

                        // Update other state as needed
                        setReadStatus(chatResp.readStatus || { user: false, admin: false });
                        setTotalPages(chatResp.totalPages || 1);

                        // Update user info including activity status
                        if (chatResp.userInfo) {
                            setUserInfo(prevInfo => ({
                                ...prevInfo,
                                u_name: chatResp.userInfo.u_name || prevInfo.u_name,
                                u_profile: chatResp.userInfo.u_profile || prevInfo.u_profile,
                                u_email: chatResp.userInfo.u_email || prevInfo.u_email,
                                _id: chatResp.userInfo._id || prevInfo._id,
                                // Update activity status from userInfo
                                isActive: chatResp.userInfo.chatScreen === true
                            }));
                        }
                    }
                } catch (error) {
                    console.error('Error refreshing after send:', error);
                }

                // Scroll to bottom to show new message
                setTimeout(scrollToBottom, 300);
            } else {
                toast.error(resp.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setLoader(false);
        }
    };

    // Scroll to bottom of chat
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Manual refresh button handler
    const handleManualRefresh = () => {
        refreshMessages();
    };

    return (
        <div className="px-3 sm:px-4 lg:ml-[4.4rem] w-full text-main-font bg-main-bg mt-16 md:mt-20">
            <div className="w-full mx-auto bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-[calc(100vh-8rem)]">
                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-main-blue to-primary-blue text-white flex justify-between items-center">
                    <div className="flex items-center">
                        <button
                            onClick={() => navigate('/manage/contact/chat')}
                            className="mr-4 hover:bg-white/10 rounded-full p-1"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* User info section - clickable to navigate to user profile */}
                        <div
                            className="flex items-center cursor-pointer hover:bg-white/10 px-3 py-1 rounded-full"
                            onClick={navigateToUserProfile}
                        >
                            <div className="relative">
                                {userInfo.u_profile ? (
                                    <img
                                        src={userInfo.u_profile}
                                        alt={userInfo.u_name}
                                        className="w-10 h-10 rounded-full object-cover border-2 border-white"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white text-lg font-medium">
                                        {userInfo.u_name?.charAt(0) || 'U'}
                                    </div>
                                )}

                                {/* Active status indicator */}
                                <div
                                    className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${userInfo.isActive ? 'bg-green-400' : 'bg-gray-400'}`}
                                    title={userInfo.isActive ? 'Online' : 'Offline'}
                                ></div>
                            </div>

                            <div className="ml-3">
                                <h2 className="text-xl font-bold">{userInfo.u_name || 'User'}</h2>
                                <div className="flex items-center text-xs text-white/80">
                                    {userInfo.isActive ? (
                                        <span className="flex items-center">
                                            <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5"></span>
                                            Online
                                        </span>
                                    ) : (
                                        <span className="flex items-center">
                                            <span className="w-2 h-2 bg-gray-400 rounded-full mr-1.5"></span>
                                            Offline
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">

                        {/* User seen status indicator */}
                        <div className="flex items-center bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                            <span className="mr-1.5">Status:</span>
                            {readStatus.user ? (
                                <span className="flex items-center text-green-300">
                                    <IoCheckmarkDone className="mr-1" />
                                    Seen
                                </span>
                            ) : (
                                <span className="flex items-center text-yellow-300">
                                    <IoMdCheckmark className="mr-1" />
                                    Not seen
                                </span>
                            )}
                        </div>

                        {/* Refresh button */}
                        <button
                            onClick={handleManualRefresh}
                            disabled={loader || isRefreshing}
                            className={`p-1.5 rounded-full ${(loader || isRefreshing) ? 'bg-white/10 cursor-not-allowed' : 'bg-white/20 hover:bg-white/30'} transition-colors`}
                            title="Refresh messages"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Chat Messages Area with typing indicator */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 relative">
                    {/* Silent refresh indicator */}
                    {isRefreshing && !loader && (
                        <div className="absolute top-2 right-2 bg-white/80 rounded-full px-2 py-1 z-10 flex items-center text-main-subfont text-xs shadow-sm">
                            <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Updating...
                        </div>
                    )}

                    {/* User active indicator for mobile */}
                    {userInfo.isActive && (
                        <div className="sm:hidden absolute top-2 left-2 bg-green-500/20 rounded-full px-2 py-1 z-10 flex items-center text-green-800 text-xs shadow-sm">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                            User is active now
                        </div>
                    )}

                    {loader && messages.length === 0 ? (
                        <Loader loader={true} />
                    ) : (
                        <>
                            {/* Load More Button */}
                            {page < totalPages && (
                                <div className="flex justify-center mb-4">
                                    <button
                                        onClick={loadMoreMessages}
                                        disabled={loadingMore}
                                        className="text-main-blue bg-main-bg px-4 py-2 rounded-lg text-sm hover:bg-main-thHover transition-colors"
                                    >
                                        {loadingMore ? (
                                            <span className="flex items-center">
                                                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Loading...
                                            </span>
                                        ) : `Load Earlier Messages (Page ${page} of ${totalPages})`}
                                    </button>
                                </div>
                            )}

                            {/* Messages */}
                            {messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-main-subfont">
                                    <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    <p>No messages yet. Start the conversation!</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {messages.map((message, index) => (
                                        <div
                                            key={message._id || index}
                                            className={`flex ${message.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div
                                                className={`max-w-[75%] rounded-lg p-3 ${message.sender === 'admin'
                                                    ? 'bg-main-blue text-white rounded-tr-none'
                                                    : 'bg-white border border-gray-200 rounded-tl-none'
                                                    }`}
                                            >
                                                {message.text && <p className="break-words">{message.text}</p>}
                                                {message.image && (
                                                    <a href={message.image} target="_blank" rel="noopener noreferrer" className="block mt-2">
                                                        <img
                                                            src={message.image}
                                                            alt="Message attachment"
                                                            className="max-h-60 rounded-md object-contain"
                                                        />
                                                    </a>
                                                )}
                                                <div className={`flex items-center justify-end text-xs mt-1 ${message.sender === 'admin' ? 'text-blue-100' : 'text-gray-500'}`}>
                                                    <span>{new Date(message.timestamp).toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* User typing indicator */}
                                    {userInfo.isActive && (
                                        <div className="flex justify-start">
                                            <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-2 px-4 flex items-center">
                                                <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div ref={messagesEndRef} />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Message Input */}
                <div className="p-4 border-t">
                    <div className="flex flex-col">
                        {/* Image preview */}
                        {imagePreview && (
                            <div className="mb-3 relative inline-block">
                                <img src={imagePreview} alt="Preview" className="h-20 rounded-md border" />
                                <button
                                    type="button"
                                    onClick={removeImage}
                                    className="absolute -top-2 -right-2 bg-main-delete text-white rounded-full p-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        )}

                        <div className="flex items-center">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="p-2 text-main-subfont hover:text-main-blue rounded-full"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageChange}
                                accept="image/jpeg,image/jpg,image/png"
                                className="hidden"
                            />

                            <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type a message..."
                                className="flex-1 border border-gray-300 rounded-full px-4 py-2 mx-2 focus:outline-none focus:ring-2 focus:ring-main-blue/30 focus:border-main-blue"
                                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                            />

                            <button
                                onClick={sendMessage}
                                disabled={loader || (!newMessage.trim() && !image)}
                                className={`p-2 rounded-full ${loader || (!newMessage.trim() && !image)
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-main-blue text-white hover:bg-blue-600'
                                    }`}
                            >
                                {loader ? (
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatRead;